import * as React from 'react';
import { __ } from '@bart.sk-eshop/react-i18n';
import styled from 'styled-components';

interface GoogleReviewProps {
  reviewData: {
    id: number;
    web_shop_id: number;
    user_picture_url: string;
    user_name: string;
    rating: number;
    review_text: string;
    review_created_at: string;
    created_at: string;
  };
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const Background = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 1;
  transition: background-color 0.1s ease 0s;
  /* White card — the page background behind is grey. */
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 8px;
  padding: 24px;
  border: 1px solid transparent;
  outline: none !important;
`;

const AuthorBlock = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-wrap: nowrap;
  max-width: 100%;
  gap: 12px;
`;

const BottomBlock = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: flex-start;
`;

const AvatarWrapper = styled.div`
  align-self: flex-start;
`;

const AvatarWrapper2 = styled.div`
  width: 40px;
  height: 40px;
  position: relative;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
`;

const AvatarBackgroundWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: inherit;
  height: inherit;
  background: linear-gradient(
    rgb(229, 229, 229) 0%,
    rgba(203, 203, 203, 0.15) 100%
  );
  transition: opacity 1s ease-out 0s;
  opacity: 1;
  border-radius: 50%;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  object-fit: cover;
  width: inherit !important;
  height: inherit !important;
`;

const IconWrapper = styled.div`
  bottom: -6px;
  right: -6px;
  position: absolute;
`;

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  padding: 2px;
  background: white;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GoogleIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const NameDateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: rgb(17, 17, 17);
`;

const Name = styled.div`
  font-size: 15px;
  line-height: 18px;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const LikeImage = styled.div`
  display: flex;
  align-items: center;
`;

const DateWrapper = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: rgba(17, 17, 17, 0.5);
`;

const StarsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

const StarIcon = styled.svg`
  width: 18px;
  height: 18px;
`;

const ReviewWrapper = styled.div`
  font-size: 16px;
  line-height: 23px;
  color: rgb(17, 17, 17);
`;

const VerifiedBadge = styled.img`
  width: 16px;
  height: 16px;
`;

const MoreText = styled.p`
  margin: 0;
  cursor: pointer;
  text-decoration-line: underline;
`;

export const GoogleReview = ({ reviewData }: GoogleReviewProps) => {
  const [isOpened, setIsOpened] = React.useState(false);

  const {
    user_name,
    user_picture_url,
    rating,
    review_text,
    review_created_at,
  } = reviewData;

  const formatTimeAgo = (dateString: string): string => {
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysAgo = Math.floor(
      (Date.now() - new Date(dateString).getTime()) / msPerDay,
    );

    if (daysAgo < 7) return `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
    if (daysAgo < 30) {
      const weeks = Math.floor(daysAgo / 7);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }
    if (daysAgo < 365) {
      const months = Math.floor(daysAgo / 30);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }

    const years = Math.floor(daysAgo / 365);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  };

  const onMoreClick = () => {
    setIsOpened(!isOpened);
  };

  const reviewTextFull = review_text;
  const reviewTextPartial =
    review_text.length > 200 ? review_text.slice(0, 200) : null;

  return (
    <Wrapper>
      <Background>
        <AuthorBlock>
          <AvatarWrapper>
            <AvatarWrapper2>
              <AvatarContainer>
                <AvatarBackgroundWrapper>
                  <AvatarImage src={user_picture_url} alt={user_name} />
                </AvatarBackgroundWrapper>
              </AvatarContainer>
              <IconWrapper>
                <IconContainer>
                  <GoogleIcon
                    src="/images/icons/google.svg"
                    alt="Google icon"
                  />
                </IconContainer>
              </IconWrapper>
            </AvatarWrapper2>
          </AvatarWrapper>
          <NameDateWrapper>
            <NameWrapper>
              <Name>{user_name}</Name>
              <LikeImage>
                <VerifiedBadge
                  src="/images/icons/google-verified.svg"
                  alt="Verified"
                />
              </LikeImage>
            </NameWrapper>
            <DateWrapper>{formatTimeAgo(review_created_at)}</DateWrapper>
          </NameDateWrapper>
        </AuthorBlock>

        <BottomBlock>
          <StarsWrapper>
            {Array.from({ length: 5 }, (_, i) => {
              const fillPercentage = Math.max(0, Math.min(1, rating - i)) * 100;
              const gradientId = `starGradient-${i}`;

              return (
                <StarIcon
                  key={i}
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient id={gradientId}>
                      <stop offset={`${fillPercentage}%`} stopColor="#fbbc04" />
                      <stop offset={`${fillPercentage}%`} stopColor="#e0e0e0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2
                        9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                    fill={`url(#${gradientId})`}
                  />
                </StarIcon>
              );
            })}
          </StarsWrapper>
          <ReviewWrapper>
            {reviewTextPartial ? (
              <div>
                <div>
                  {isOpened ? reviewTextFull : `${reviewTextPartial}...`}
                </div>
                <MoreText onClick={onMoreClick}>
                  {' '}
                  {isOpened ? __('Zobraziť menej') : __('Zobraziť viac')}{' '}
                </MoreText>{' '}
              </div>
            ) : (
              reviewTextFull
            )}
          </ReviewWrapper>
        </BottomBlock>
      </Background>
    </Wrapper>
  );
};
